import { appDefaults }    from './../.././../defaults'
import { http }           from '@/http'
import GeolocationHandler from './../../GeolocationHandler'

export default class BaseResource {

  /**
   *
   * @param resource
   * @param defaults
   */
  constructor (resource, defaults) {
    if (!resource) {
      throw new Error('Resource must be specified!')
    }

    this.resource = resource

    if (!defaults) {
      defaults = appDefaults
    }

    this.limit   = defaults.api.limit
    this.offset  = defaults.api.offset
    this.where   = ''
    this.orderBy = ''

    // CRUD urls
    this.urlGetAutocompleteData = 'Table/' + this.resource +
      '/GetAutocompleteData'
    this.urlGetById             = 'Table/' + this.resource + '/GetById'
    this.urlPaged               = 'Table/' + this.resource + '/GetPaged'
    this.urlCreate              = 'Table/' + this.resource + '/Create'
    this.urlUpdate              = 'Table/' + this.resource + '/Update'
    this.urlDelete              = 'Table/' + this.resource + '/Delete'

    // specialni metoda - obchodni pravidla - business rules
    this.urlBusinessRules = 'Table/' + this.resource + '/CallBr'
    this.urlReport        = 'Table/' + this.resource + '/Report'

    // specalni obchodni pravidlo predavane jako GET parametr v pozadavcich
    // insert, update, delete
    this._afterSaveRec = null

    this._idtEntity = null
    this._def       = null

    this.geolocationHandler = GeolocationHandler
  }

  /**
   *
   * @returns {{limit: number|*, offset: number|*}}
   */
  defaults () {
    return {
      limit:  this.limit,
      offset: this.offset
    }
  }

  /**
   *
   * @param id
   * @param args
   * @return {AxiosPromise<any> | IDBRequest | Promise<void>}
   */
  getById (id, args = {}) {
    let params = '?'

    Object
      .keys(args)
      .map(key => {
        params += key + '=' + args[key]
      })

    return this.geolocationHandler.getPromise().then(() => {
      params = this.geolocationHandler.extendParams(params)

      return http.get(this._constructUrl(this.urlGetById + '/' + id + params))
    })
  }

  /**
   *
   * @param params
   * @return {Promise<any>}
   */
  getAutocompleteData (params) {
    return this.geolocationHandler.getPromise().then(() => {
      this.geolocationHandler.extendArgs(params.data)

      let url = this._constructAutocompleteUrl(
        this.urlGetAutocompleteData, params.queries)

      return http.post(this._constructUrl(url), params.data)
    })
  }

  /**
   *
   * @param args
   * @return {AxiosPromise<any> | IDBRequest | Promise<void>}
   */
  find (args) {
    let params = this._prepareParams(args)

    if (args.searchText) {
      params.pageSize   = this.limit
      params.searchText = args.searchText
    }

    if (args.hasOwnProperty('condition') && args.condition) {
      params.where = args.where + args.condition
    }

    if (args.hasOwnProperty('whereCondition') && args.whereCondition) {
      let where    = args.hasOwnProperty('where') ? args.where : ''
      params.where = where + args.whereCondition
    }

    if (!args.hasOwnProperty('idtEntity')) {
      alert('Missing argument args.idtEntity!')
    }

    params.idtEntity = args.idtEntity

    return this.geolocationHandler.getPromise().then(() => {
      this.geolocationHandler.extendArgs(params)

      return http.get(this.urlPaged, {
        params: params
      })
    })
  }

  /**
   *
   * @param args
   * @return {AxiosPromise<any> | * | void}
   */
  create (args) {
    if (!args.hasOwnProperty('data')) {
      throw Error('create method argument must contain data property!')
    }

    return this.geolocationHandler.getPromise().then(() => {
      this.geolocationHandler.extendArgs(args.data)

      return http.post(
        this._constructUrl(this.urlCreate), args.data)
    })
  }

  /**
   *
   * @param args
   * @return {Promise<AxiosResponse<any> | never>}
   */
  update (args) {
    if (!args.hasOwnProperty('data')) {
      throw Error('update method argument must contain data property!')
    }

    if (!args.hasOwnProperty('ID')) {
      throw Error('update method argument must contain data property!')
    }

    return this.geolocationHandler.getPromise().then(() => {
      this.geolocationHandler.extendArgs(args.data)

      return http.put(
        this._constructUrl(this.urlUpdate + '/' + args.ID),
        args.data)
    })
  }

  /**
   *
   * @param id
   * @return {AxiosPromise<any> | IDBRequest | Promise<void>}
   */
  delete (id) {
    let params = '?'

    return this.geolocationHandler.getPromise().then(() => {
      params = this.geolocationHandler.extendParams(params)

      return http.delete(
        this._constructUrl(this.urlDelete + '/' + id + params))
    })
  }

  /**
   *
   * @param args
   * @return {AxiosPromise<any> | IDBRequest | Promise<void>}
   */
  callBusinessRule (args) {
    if (!args.hasOwnProperty('ruleType') || !args.hasOwnProperty('ruleName') ||
      !args.hasOwnProperty('where')) {
      throw Error('args properties problem!')
    }

    let url   = this.urlBusinessRules,
        value = {}

    url += '?'
    url += 'brType=' + args.ruleType
    url += '&where=' + args.where

    if (args.ruleName) {
      url += '&spName=' + args.ruleName
    }

    if (args.hasOwnProperty('columnName') && args.columnName) {
      url += '&columnName=' + args.columnName
    }

    if (args.hasOwnProperty('value')) {
      value = args.value
    }

    return this.geolocationHandler.getPromise().then(() => {
      this.geolocationHandler.extendArgs(value)

      return http.post(this._constructUrl(url), value, {
        headers: {
          'Content-type': 'application/json'
        }
      })
    })
  }

  /**
   *
   * @param args
   * @return {AxiosPromise<any> | IDBRequest | Promise<void>}
   */
  callReport (args) {
    let url   = this.urlReport,
        value = {}

    url += '?'
    url += 'brType=' + args.ruleType
    url += '&where=' + args.where

    if (args.ruleName) {
      url += '&spName=' + args.ruleName
    }

    if (args.hasOwnProperty('columnName') && args.columnName) {
      url += '&columnName=' + args.columnName
    }

    if (args.hasOwnProperty('value')) {
      value = args.value
    }

    return this.geolocationHandler.getPromise().then(() => {
      this.geolocationHandler.extendArgs(value)

      return http.post(this._constructUrl(url), value, {
        headers: {
          'Content-type': 'application/json'
        }
      })
    })
  }

  /**
   *
   * @return string
   */
  getResourceName () {
    return this.resource
  }

  /**
   *
   * @param args
   * @returns {{pageSize: number|*, pageNum: number|*, where: string}}
   * @private
   */
  _prepareParams (args) {
    let params = {
      pageSize: this.limit,
      pageNum:  this.offset
    }

    if (this._def) {
      params.formIdtEntity = this._def.idtEntity
    }

    if (args.hasOwnProperty('limit') && args.limit) {
      params.pageSize = args.limit
    }

    if (args.hasOwnProperty('offset') && args.offset) {
      params.pageNum = args.offset
    }

    if (args.hasOwnProperty('where') && args.where) {
      params.where = args.where
    }

    if (args.hasOwnProperty('orderBy') && args.orderBy) {
      params.orderBy = args.orderBy
    }

    return params
  }

  /**
   *
   * @param url
   * @param glue
   * @return {*}
   * @private
   */
  _constructUrl (url, glue = null) {
    if (!glue) {
      glue = '?'
    }

    if (url.indexOf('?') > 0) {
      glue = '&'
    }

    if (this._afterSaveRec) {
      url  = url + glue + 'AfterSaveRec=' + this._afterSaveRec
      glue = '&'
    }

    if (this._idtEntity) {
      url  = url + glue + 'idtEntity=' + this._idtEntity
      glue = '&'
    }

    if (this._def) {
      url = url + glue + 'formIdtEntity=' + this._def.idtEntity
    }

    return url
  }

  /**
   *
   * @param url
   * @param queries
   * @return {*}
   * @private
   */
  _constructAutocompleteUrl (url, queries) {
    url = url + '?searchText=' + queries.searchText

    if (queries.hasOwnProperty('columnName')) {
      url = url + '&columnName=' + queries.columnName
    }

    if (queries.hasOwnProperty('idtEntity')) {
      url = url + '&idtEntity=' + queries.idtEntity
    }

    if (queries.hasOwnProperty('idtEntityStructureFrom')) {
      url = url + '&idtEntityStructureFrom=' + queries.idtEntityStructureFrom
    }
    else {
      url = url + '&idtEntityStructureFrom=0'
    }

    if (queries.hasOwnProperty('pageSize')) {
      url = url + '&pageSize=' + queries.pageSize
    }

    return url
  }

  /**
   *
   * @param def
   * @return {BaseResource}
   */
  setDef (def) {
    this._def = def

    return this
  }

  /**
   *
   * @param value
   */
  set afterSaveRec (value) {
    this._afterSaveRec = value

    return this
  }

  /**
   *
   * @param value
   */
  set idtEntity (value) {
    this._idtEntity = value

    return this
  }

}
